import { Network } from '@capacitor/network';
import { Capacitor } from '@capacitor/core';
import { ref } from 'vue';
import { isBundledMobileApp } from '@/mobile/native/apiBase';
import { mapNetworkFetchError } from '@/mobile/native/mapNetworkFetchError';
import { fetchFieldBootstrap, postFieldSync } from '@/mobile/sync/fieldSyncApi';
import {
  applyPushResultsFromSync,
  clearFieldCache,
  getLastPullAt,
  getOrCreateDeviceId,
  loadFieldCache,
  loadOutbox,
  mergeFieldCacheDelta,
  peekFieldCacheMemory,
  reconcileFieldOutboxWithServerReports,
  saveFieldCache,
  type FieldSyncCache,
} from '@/mobile/sync/fieldSyncCache';
import { slimFieldReportPushPayload } from '@/mobile/sync/fieldSyncPushPayload';
import { applyFieldSyncUserSnapshot } from '@/mobile/sync/applyFieldSyncUserSnapshot';
import { syncPendingReportsIntoCache } from '@/mobile/sync/fieldPendingReports';
import {
  startBundledFieldSyncLifecycle,
} from '@/mobile/sync/bundledFieldSyncLifecycle';
import type { FieldSyncResponse } from '@/mobile/sync/fieldSyncTypes';

const syncing = ref(false);
const lastError = ref<string | null>(null);
const lastSyncMessage = ref<string | null>(null);
const cache = ref<FieldSyncCache | null>(null);
const isOnline = ref(true);

let watcherStarted = false;
let networkHandle: { remove: () => void } | undefined;
let activeSync: Promise<FieldSyncResponse | null> | null = null;
let lastFieldSyncResponse: FieldSyncResponse | null = null;
let debouncedSyncTimer: ReturnType<typeof setTimeout> | null = null;
let idlePullTimer: ReturnType<typeof setTimeout> | null = null;
let lastSuccessfulSyncAt = 0;
let lastSuccessfulPullAt = 0;

const BUNDLED_MIN_SYNC_INTERVAL_MS = 30_000;
const BUNDLED_DEBOUNCED_SYNC_MS = 6_000;
const BUNDLED_IDLE_PULL_MS = 20 * 60 * 1000;

export type RunFieldSyncOptions = {
  /**
   * true = envia fila + pull incremental (manual, pull-to-refresh, idle).
   * false = só envia fila (APK em background).
   * Omitido: web faz pull; APK só push (ou no-op se fila vazia).
   */
  pull?: boolean;
};

function resolvePullFlag(options?: RunFieldSyncOptions): boolean {
  if (options?.pull !== undefined) {
    return options.pull;
  }

  return !isBundledMobileApp();
}

/**
 * Sync em segundo plano (APK): só envia a fila local — sem pull incremental.
 */
export function scheduleDebouncedFieldSync(delayMs = BUNDLED_DEBOUNCED_SYNC_MS): void {
  if (!isBundledMobileApp() || !Capacitor.isNativePlatform()) {
    void runFieldSync();

    return;
  }

  if (debouncedSyncTimer) {
    clearTimeout(debouncedSyncTimer);
  }

  debouncedSyncTimer = setTimeout(() => {
    debouncedSyncTimer = null;

    if (syncing.value) {
      return;
    }

    if (Date.now() - lastSuccessfulSyncAt < BUNDLED_MIN_SYNC_INTERVAL_MS) {
      return;
    }

    void runFieldSync({ pull: false }).then((response) => {
      if (response) {
        lastSuccessfulSyncAt = Date.now();
      }
    });
  }, delayMs);
}

function scheduleBundledIdlePull(): void {
  if (!isBundledMobileApp() || !Capacitor.isNativePlatform()) {
    return;
  }

  if (idlePullTimer) {
    clearTimeout(idlePullTimer);
  }

  idlePullTimer = setTimeout(() => {
    idlePullTimer = null;

    if (syncing.value) {
      scheduleBundledIdlePull();

      return;
    }

    if (Date.now() - lastSuccessfulPullAt < BUNDLED_IDLE_PULL_MS) {
      scheduleBundledIdlePull();

      return;
    }

    void runFieldSync({ pull: true }).then((response) => {
      if (response) {
        lastSuccessfulPullAt = Date.now();
      }
      scheduleBundledIdlePull();
    });
  }, BUNDLED_IDLE_PULL_MS);
}

export async function refreshFieldOnlineStatus(): Promise<boolean> {
  if (!Capacitor.isNativePlatform()) {
    isOnline.value = navigator.onLine;
    return isOnline.value;
  }

  const status = await Network.getStatus();
  isOnline.value = status.connected;

  return isOnline.value;
}

export async function pullFieldBootstrap(): Promise<FieldSyncCache | null> {
  const data = await fetchFieldBootstrap();
  await saveFieldCache(data);
  await reconcileFieldOutboxWithServerReports(data.reports);
  await applyFieldSyncUserSnapshot(data.user);
  cache.value = peekFieldCacheMemory() ?? (await loadFieldCache());
  lastSuccessfulPullAt = Date.now();

  if (!isBundledMobileApp()) {
    void import('@/mobile/sync/scheduleTreeCache').then(({ prefetchScheduleTreesForFieldCache }) =>
      prefetchScheduleTreesForFieldCache(cache.value),
    );
  }

  return cache.value;
}

async function executeFieldSync(pull: boolean): Promise<FieldSyncResponse | null> {
  syncing.value = true;
  lastError.value = null;

  try {
    const online = await refreshFieldOnlineStatus();

    if (!online) {
      lastSyncMessage.value = 'Sem conexão — alterações permanecem na fila.';
      cache.value = peekFieldCacheMemory() ?? (await loadFieldCache());

      return null;
    }

    const deviceId = await getOrCreateDeviceId();
    const lastPullAt = await getLastPullAt();
    const outbox = await loadOutbox();

    if (outbox.length === 0 && !pull) {
      lastSyncMessage.value = null;

      return null;
    }

    if (outbox.length === 0 && pull) {
      if (isBundledMobileApp()) {
        await pullFieldBootstrap();
        lastSyncMessage.value = 'Listas atualizadas.';
        lastSuccessfulSyncAt = Date.now();
        lastSuccessfulPullAt = Date.now();

        return null;
      }

      const response = await postFieldSync({
        deviceId,
        lastPullAt,
        push: [],
        pull: true,
      });

      await mergeFieldCacheDelta({
        serverTime: response.serverTime,
        summary: response.pulled.summary,
        projects: response.pulled.projects,
        reports: response.pulled.reports,
        activities: response.pulled.activities,
      });

      await applyFieldSyncUserSnapshot(response.user);

      cache.value = peekFieldCacheMemory() ?? (await loadFieldCache());
      lastSyncMessage.value = 'Listas atualizadas.';

      await reconcileFieldOutboxWithServerReports(response.pulled.reports);

      lastFieldSyncResponse = response;
      lastSuccessfulSyncAt = Date.now();
      lastSuccessfulPullAt = Date.now();

      return response;
    }

    const response = await postFieldSync({
      deviceId,
      lastPullAt,
      pull,
      push: outbox.map((item) => {
        if (item.operation.operation === 'delete') {
          return {
            ...item.operation,
            payload: item.operation.payload ?? { type: 'rdo' },
          };
        }

        const slim = slimFieldReportPushPayload(item.operation.payload);

        return {
          ...item.operation,
          payload: slim.payload,
        };
      }),
    });

    const { synced, failed } = await applyPushResultsFromSync(response);

    if (pull) {
      if (isBundledMobileApp()) {
        await pullFieldBootstrap();
      } else {
        await mergeFieldCacheDelta({
          serverTime: response.serverTime,
          summary: response.pulled.summary,
          projects: response.pulled.projects,
          reports: response.pulled.reports,
          activities: response.pulled.activities,
        });

        await reconcileFieldOutboxWithServerReports(response.pulled.reports);
      }
    }

    await applyFieldSyncUserSnapshot(response.user);

    let remainingOutbox = await loadOutbox();

    if (remainingOutbox.length > 0 && failed > 0 && !isBundledMobileApp()) {
      const bootstrap = await fetchFieldBootstrap();

      await mergeFieldCacheDelta({
        serverTime: bootstrap.serverTime,
        summary: bootstrap.summary,
        projects: bootstrap.projects,
        reports: bootstrap.reports,
        activities: bootstrap.activities,
      });

      await applyFieldSyncUserSnapshot(bootstrap.user);

      const reconciledFromBootstrap = await reconcileFieldOutboxWithServerReports(
        bootstrap.reports,
      );

      if (reconciledFromBootstrap > 0) {
        remainingOutbox = await loadOutbox();
      }
    }

    cache.value = peekFieldCacheMemory() ?? (await loadFieldCache());

    remainingOutbox = await loadOutbox();

    const reconciledFromPull = pull ? synced : 0;

    if (failed > 0) {
      const firstError = response.pushResults.find(
        (result) => result.status === 'error' || result.status === 'conflict',
      );
      const detail =
        firstError?.status === 'conflict'
          ? 'Conflito de versão. Recarregue o relatório e tente novamente.'
          : (firstError?.message ?? 'Alguns itens não foram enviados.');
      lastError.value = detail;
      lastSyncMessage.value = detail;
    } else if (synced > 0) {
      lastSyncMessage.value =
        synced === 1 ? 'RDO enviado ao servidor.' : `${synced} RDOs sincronizados.`;
    } else if (pull) {
      lastSyncMessage.value = 'Listas atualizadas.';
    } else if (remainingOutbox.length > 0) {
      const firstError = response.pushResults.find(
        (result) => result.status === 'error' || result.status === 'conflict',
      );
      lastError.value =
        firstError?.message ??
        'RDO na fila local. Toque em Sincronizar ou veja o erro no card.';
      lastSyncMessage.value = lastError.value;
    } else {
      lastSyncMessage.value = pull ? 'Listas atualizadas.' : 'Tudo sincronizado.';
    }

    if (!isBundledMobileApp()) {
      void import('@/mobile/offline/prefetchFieldPages').then(
        ({ prefetchFieldInertiaPages }) => prefetchFieldInertiaPages(),
      );
    }

    lastFieldSyncResponse = response;

    lastSuccessfulSyncAt = Date.now();
    if (pull) {
      lastSuccessfulPullAt = Date.now();
    }

    return response;
  } catch (error) {
    lastError.value = mapNetworkFetchError(error, 'Falha na sincronização.').message;
    lastSyncMessage.value = lastError.value;
    cache.value = peekFieldCacheMemory() ?? (await loadFieldCache());

    return null;
  } finally {
    syncing.value = false;
  }
}

async function runFieldSyncPass(
  allowRetry: boolean,
  pull: boolean,
): Promise<FieldSyncResponse | null> {
  if (activeSync) {
    await activeSync;

    if (allowRetry && !isBundledMobileApp() && (await loadOutbox()).length > 0) {
      return runFieldSyncPass(false, pull);
    }

    return lastFieldSyncResponse;
  }

  activeSync = executeFieldSync(pull).finally(() => {
    activeSync = null;
  });

  const response = await activeSync;

  if (allowRetry && !isBundledMobileApp() && (await loadOutbox()).length > 0) {
    return runFieldSyncPass(false, pull);
  }

  return response;
}

export async function runFieldSync(
  options?: RunFieldSyncOptions,
): Promise<FieldSyncResponse | null> {
  return runFieldSyncPass(true, resolvePullFlag(options));
}

/** Pull incremental + envio da fila (botão Sincronizar / pull-to-refresh). */
export { scheduleSoftFieldPull, runSoftFieldPullIfStale } from '@/mobile/sync/bundledFieldSyncLifecycle';

export async function runFieldPullSync(): Promise<FieldSyncResponse | null> {
  return runFieldSync({ pull: true });
}

export async function reloadFieldCache(options?: {
  skipPending?: boolean;
}): Promise<FieldSyncCache | null> {
  if (
    isBundledMobileApp()
    && options?.skipPending
    && cache.value?.reports?.length
  ) {
    return cache.value;
  }

  if (!options?.skipPending) {
    await syncPendingReportsIntoCache();
  }

  cache.value = peekFieldCacheMemory() ?? (await loadFieldCache());

  return cache.value;
}

export async function initializeFieldSync(): Promise<void> {
  await syncPendingReportsIntoCache();
  cache.value = peekFieldCacheMemory() ?? (await loadFieldCache());
  await refreshFieldOnlineStatus();

  if (!isOnline.value) {
    return;
  }

  const lastPullAt = await getLastPullAt();
  const needsFullLoad =
    !lastPullAt ||
    !cache.value ||
    (cache.value.projects.length === 0 && cache.value.reports.length === 0);

  if (needsFullLoad || !isBundledMobileApp()) {
    try {
      await pullFieldBootstrap();
    } catch (error) {
      console.warn('[field-sync] bootstrap falhou — usando cache local.', error);
      cache.value = await reloadFieldCache();
    }
  } else {
    void runFieldSync({ pull: false });
  }
}

export function startFieldSyncWatcher(): void {
  if (watcherStarted) {
    return;
  }

  watcherStarted = true;

  if (!Capacitor.isNativePlatform()) {
    const onOnline = () => {
      isOnline.value = true;
      void runFieldSync();
    };

    window.addEventListener('online', onOnline);
    networkHandle = {
      remove: () => window.removeEventListener('online', onOnline),
    };

    return;
  }

  void Network.addListener('networkStatusChange', (status) => {
    isOnline.value = status.connected;
    if (status.connected && !syncing.value) {
      if (isBundledMobileApp()) {
        scheduleDebouncedFieldSync(3_000);
      } else {
        void runFieldSync();
        void import('@/mobile/offline/prefetchFieldPages').then(
          ({ prefetchFieldInertiaPages }) => prefetchFieldInertiaPages(),
        );
      }
    }
  }).then((handle) => {
    networkHandle = handle;
  });

  if (isBundledMobileApp()) {
    scheduleBundledIdlePull();
    startBundledFieldSyncLifecycle();
  }
}

export function stopFieldSyncWatcher(): void {
  networkHandle?.remove();
  networkHandle = undefined;
  watcherStarted = false;

  if (idlePullTimer) {
    clearTimeout(idlePullTimer);
    idlePullTimer = null;
  }
}

export function readFieldSyncLastError(): string | null {
  return lastError.value;
}

export function useFieldSync() {
  return {
    syncing,
    lastError,
    lastSyncMessage,
    cache,
    isOnline,
    initialize: initializeFieldSync,
    reloadCache: (options?: { skipPending?: boolean }) => reloadFieldCache(options),
    runSync: (options?: RunFieldSyncOptions) => runFieldSync(options),
    runPullSync: runFieldPullSync,
    pullBootstrap: pullFieldBootstrap,
    clearCache: clearFieldCache,
    refreshOnline: refreshFieldOnlineStatus,
  };
}
